import { useEffect, useState } from 'react'
import { adminApi } from '../../api/adminApi'
import { Briefcase, Users, CheckCircle, Clock, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'

interface Stats {
  pendingJobs: number
  totalJobs: number
  totalUsers: number
  totalReferrals: number
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      adminApi.getJobs({ status: 'PENDING', page: 0, size: 1 }),
      adminApi.getJobs({ page: 0, size: 1 }),
      adminApi.getUsers({ page: 0, size: 1 }),
    ])
      .then(([pendingRes, allJobsRes, usersRes]) => {
        setStats({
          pendingJobs: pendingRes.data.data?.totalElements || 0,
          totalJobs: allJobsRes.data.data?.totalElements || 0,
          totalUsers: usersRes.data.data?.totalElements || 0,
          totalReferrals: 0,
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const statCards = [
    { label: 'Pending Jobs', value: stats?.pendingJobs, icon: <Clock size={22} className="text-yellow-500" />, link: '/admin/jobs', bg: 'bg-yellow-50' },
    { label: 'Total Jobs', value: stats?.totalJobs, icon: <Briefcase size={22} className="text-blue-500" />, link: '/admin/jobs', bg: 'bg-blue-50' },
    { label: 'Total Users', value: stats?.totalUsers, icon: <Users size={22} className="text-green-500" />, link: '/admin/users', bg: 'bg-green-50' },
    { label: 'Referrals', value: stats?.totalReferrals, icon: <CheckCircle size={22} className="text-primary-500" />, link: '/admin/jobs', bg: 'bg-primary-50' },
  ]

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card) => (
          <Link
            key={card.label}
            to={card.link}
            className={`${card.bg} rounded-xl p-5 no-underline hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                {card.icon}
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{card.value ?? '–'}</p>
            <p className="text-sm text-gray-500 mt-0.5">{card.label}</p>
          </Link>
        ))}
      </div>

      <div className="card">
        <h2 className="font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/jobs" className="btn-primary text-sm">
            Review Pending Jobs
          </Link>
          <Link to="/admin/users" className="btn-secondary text-sm">
            Manage Users
          </Link>
          <Link to="/admin/settings" className="btn-secondary text-sm">
            App Settings
          </Link>
        </div>
      </div>
    </div>
  )
}
